import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Save, RotateCcw, Check, Copy, Trash2, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ParaphraseModule({
  order,
  sessionId,
  selectedModel,
  onConfirm,
  onTokensUsed,
  previousParagraphs = [],
  maxParagraphs = 20,
  customSettings,
  initialParagraph = null
}) {
  const [originalText, setOriginalText] = useState('');
  const [paraphrasedContent, setParaphrasedContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [paragraphId, setParagraphId] = useState(null);
  const [versions, setVersions] = useState([]);
  const [wordCount, setWordCount] = useState({ original: 0, paraphrased: 0 });
  
  // Load initial paragraph if provided (from session restore)
  useEffect(() => {
    if (initialParagraph) {
      setParaphrasedContent(initialParagraph.content || '');
      setOriginalText(initialParagraph.original_text || '');
      setParagraphId(initialParagraph.id);
      setConfirmed(true);
    }
  }, [initialParagraph]);
  
  // Update word counts
  useEffect(() => {
    const originalWords = originalText.trim().split(/\s+/).filter(w => w.length > 0);
    const paraphrasedWords = paraphrasedContent.trim().split(/\s+/).filter(w => w.length > 0);
    setWordCount({
      original: originalWords.length,
      paraphrased: paraphrasedWords.length
    });
  }, [originalText, paraphrasedContent]);

  const handleParaphrase = async () => {
    if (!originalText.trim()) {
      toast.error('Please enter text to paraphrase');
      return;
    }

    if (originalText.trim().length < 50) {
      toast.error('Please enter at least 50 characters');
      return;
    }

    setLoading(true);
    
    try {
      // Build context from previous paragraphs
      const context = previousParagraphs.map(p => ({
        content: p.content,
        order: p.order
      }));

      const response = await axios.post(
        `${API}/admin/generation/paraphrase`,
        {
          original_text: originalText,
          session_id: sessionId,
          model_provider: selectedModel.provider,
          model_name: selectedModel.name,
          context: context,
          custom_settings: customSettings
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        const newContent = response.data.content;
        setParaphrasedContent(newContent);
        
        // Add to versions history
        setVersions(prev => [...prev, newContent]);
        
        // Track tokens
        if (response.data.tokens_used) {
          onTokensUsed(response.data.tokens_used);
        }
        
        toast.success('Text paraphrased successfully!');
      } else {
        toast.error('Failed to paraphrase text');
      }
    } catch (error) {
      console.error('Error paraphrasing:', error);
      toast.error(error.response?.data?.detail || 'Failed to paraphrase text');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = () => {
    if (window.confirm('Generate a new paraphrase? This will replace the current version.')) {
      handleParaphrase();
    }
  };

  const handleConfirm = () => {
    if (!paraphrasedContent.trim()) {
      toast.error('Please paraphrase text before confirming');
      return;
    }

    const tempId = `temp-${Date.now()}-${order}`;
    const paragraphData = {
      id: paragraphId || tempId,
      order,
      content: paraphrasedContent.trim(),
      original_text: originalText.trim(),
      session_id: sessionId,
      main_idea: null
    };

    setParagraphId(tempId);
    setConfirmed(true);
    onConfirm(paragraphData);
    toast.success(`Paragraph ${order} confirmed!`);
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset this paragraph?')) {
      setOriginalText('');
      setParaphrasedContent('');
      setConfirmed(false);
      setVersions([]);
      toast.info('Paragraph reset');
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(paraphrasedContent);
    toast.success('Copied to clipboard');
  };

  const handleRevertVersion = (index) => {
    if (index >= 0 && index < versions.length) {
      setParaphrasedContent(versions[index]);
      toast.success(`Reverted to version ${index + 1}`);
    }
  };

  const isDisabled = order > previousParagraphs.length + 1 || (previousParagraphs.length >= maxParagraphs);

  if (isDisabled) {
    return (
      <div className="admin-card opacity-50">
        <div className="text-center text-white py-8">
          <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="text-lg">Complete previous paragraphs first</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`admin-card ${confirmed ? 'border-4 border-green-500' : ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-2xl font-bold text-white flex items-center gap-2">
          <Sparkles className="w-6 h-6" />
          Paragraph {order} - AI Paraphrasing
        </h3>
        {confirmed && (
          <div className="flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg font-bold">
            <Check className="w-5 h-5" />
            CONFIRMED
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="mb-4 p-4 bg-purple-100 border-2 border-purple-400 rounded-lg">
        <p className="text-purple-900 font-semibold">
          🔄 Paste your existing text. AI will rewrite it to avoid plagiarism while preserving meaning.
        </p>
      </div>

      {/* Original Text Input */}
      <div className="mb-4">
        <label className="block text-white font-bold text-lg mb-2">
          Original Text to Paraphrase
        </label>
        <textarea
          value={originalText}
          onChange={(e) => setOriginalText(e.target.value)}
          disabled={confirmed || loading}
          placeholder="Paste the text you want to paraphrase here..."
          className="w-full h-48 px-4 py-3 border-2 border-[#8B6F47] rounded focus:outline-none focus:border-[#6B4423] text-gray-900 resize-vertical"
        />
        <div className="flex justify-between mt-2 text-sm text-white">
          <span>Characters: {originalText.length}</span>
          <span>Words: {wordCount.original}</span>
        </div>
      </div>

      {/* Paraphrase Button */}
      {!confirmed && (
        <div className="mb-4">
          <button
            onClick={handleParaphrase}
            disabled={loading || !originalText.trim() || originalText.trim().length < 50}
            className="retro-button flex items-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Paraphrasing...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Paraphrase with AI
              </>
            )}
          </button>
        </div>
      )}

      {/* Paraphrased Content */}
      {paraphrasedContent && (
        <div className="mb-4">
          <label className="block text-white font-bold text-lg mb-2">
            Paraphrased Content (Editable)
          </label>
          <textarea
            value={paraphrasedContent}
            onChange={(e) => setParaphrasedContent(e.target.value)}
            disabled={confirmed}
            className="w-full h-48 px-4 py-3 border-2 border-green-500 rounded focus:outline-none focus:border-green-600 text-gray-900 resize-vertical bg-green-50"
          />
          <div className="flex justify-between mt-2 text-sm text-white">
            <span>Characters: {paraphrasedContent.length}</span>
            <span>Words: {wordCount.paraphrased}</span>
          </div>
        </div>
      )}

      {/* Version History */}
      {versions.length > 1 && !confirmed && (
        <div className="mb-4 p-3 bg-[#8B6F47] rounded">
          <h4 className="text-white font-bold mb-2">Version History ({versions.length})</h4>
          <div className="flex gap-2 flex-wrap">
            {versions.map((_, index) => (
              <button
                key={index}
                onClick={() => handleRevertVersion(index)}
                className="px-3 py-1 bg-[#F4E4C1] text-[#6B4423] rounded text-sm hover:bg-white transition-colors"
              >
                Version {index + 1}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3 flex-wrap">
        {!confirmed ? (
          <>
            <button
              onClick={handleConfirm}
              disabled={!paraphrasedContent.trim()}
              className="retro-button flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Check className="w-5 h-5" />
              Confirm Paragraph
            </button>
            {paraphrasedContent && (
              <button
                onClick={handleRegenerate}
                disabled={loading}
                className="retro-button flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
              >
                <RotateCcw className="w-5 h-5" />
                Regenerate
              </button>
            )}
            <button
              onClick={() => {
                setOriginalText('');
                setParaphrasedContent('');
                setVersions([]);
              }}
              disabled={!originalText && !paraphrasedContent}
              className="retro-button flex items-center gap-2 bg-gray-600 hover:bg-gray-700 disabled:opacity-50"
            >
              <Trash2 className="w-5 h-5" />
              Clear All
            </button>
          </>
        ) : (
          <>
            <button
              onClick={handleReset}
              className="retro-button flex items-center gap-2 bg-yellow-600 hover:bg-yellow-700"
            >
              <Save className="w-5 h-5" />
              Edit Paragraph
            </button>
            <button
              onClick={handleCopy}
              className="retro-button flex items-center gap-2"
            >
              <Copy className="w-5 h-5" />
              Copy
            </button>
          </>
        )}
      </div>

      {/* Display Confirmed Content */}
      {confirmed && (
        <div className="mt-6 space-y-4">
          <div className="p-4 bg-gray-100 rounded border-2 border-gray-400">
            <h4 className="font-bold text-gray-700 mb-2">Original Text:</h4>
            <p className="text-gray-600 whitespace-pre-wrap leading-relaxed text-sm">{originalText}</p>
          </div>
          <div className="p-4 bg-white rounded border-2 border-green-500">
            <h4 className="font-bold text-[#6B4423] mb-2">Paraphrased Content:</h4>
            <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">{paraphrasedContent}</p>
          </div>
        </div>
      )}
    </div>
  );
}
